import { isUserAuthenticated } from "@/middleware/auth";
import { stopChunkedStreamOnDelete } from "./chunkedStreamTeardown";
import {
  addChatToLocalDB,
  getChatFromLocalDB,
  updateChatInLocalDB,
  removePinnedMessageIdFromChatInLocalDB,
} from "@/indexedDB/services/chats";
// import { playNotificationSound } from "@/utils/audioUtils";
import {
  decryptMessageContent,
  decryptServiceMessageContent,
} from "@/middleware/crypto";
import { tryDecrypt } from "@/middleware/decryptionError";
import { isAppVisible } from "@/utils/visibilityState";
import { createLogger } from "@/utils/debug";
import {
  ChatEntity,
  MessageContentEntity,
  MessageEntity,
  MessageSendingStatus,
} from "@/indexedDB/types";
import { extractTimestampFromUUID7 } from "@/utils/uuid7";
import {
  addMessageToLocalDB,
  deleteMessageFromLocalDBWithRetry,
  getLatestMessagesByChatIdFromLocalDB,
  getMessageByServerIdFromLocalDB,
  updateMessageByServerIdInLocalDB,
} from "@/indexedDB/services/messages";
import { useNavigationState } from "@/zustand/NavigationState";
import { SocketEventActionType, MessageEventType } from "./types";
import { MessageCategory } from "@/server/types/message";
import { MemberRole } from "@/server/types/member";
import { processMessageLink } from "@/middleware/links";
import { resetChatUnreadMessages } from "@/middleware/counters";
import { maybeRotateP2PForForwardSecrecy } from "@/middleware/chats";
import {
  addLinkToLocalDB,
  deleteLinkByIdFromLocalDB,
} from "@/indexedDB/services/links";

import {
  removeFileAttachmentFromLocalDB,
  removeMediaAttachmentFromLocalDB,
} from "@/indexedDB/services/attachments";

import { ChatType } from "@/server/types/chat";
import { showMessageNotification } from "@/utils/appNotification";
import { useUserAvatarMapState } from "@/zustand/UserAvatarMapState";
import {
  removePinnedMessageByServerIdFromLocalDB,
  updatePinnedMessageByServerIdInLocalDB,
} from "@/indexedDB/services/pinnedMessages";
import { featureFlags } from "@/config/featureFlags";
import { fetchChatById } from "@/indexedDB/services/syncApiClient";
import { mapApiChatToEntity } from "@/indexedDB/services/syncMappers";

const log = createLogger("MessageHandler");

// TO DO => Handle the event only if msg not present, not decrypted - cross device check
export const handleMessageEvent = async (data: MessageEventType) => {
  // extra check if auth is present
  const userFingerPrint = isUserAuthenticated();
  if (!userFingerPrint) {
    return;
  }

  let chatDetails = await getChatFromLocalDB(data.message.chat_id);
  if (!chatDetails) {
    // When the new sync engine is active, fetch unknown chat metadata
    // from the server so the message can still be processed.
    if (featureFlags.useNewSync) {
      try {
        // Normalize the server response into the local ChatEntity shape
        // (maps chatId→id, sets the Dexie primary key, init/sync fields)
        // so it matches every other sync write path.
        const fetchedChat = mapApiChatToEntity(
          await fetchChatById(data.message.chat_id),
        );
        await addChatToLocalDB(fetchedChat);
        chatDetails = fetchedChat;
      } catch (err) {
        // Failed to fetch the unknown chat — can't process the message without
        // it. Log (chatId/messageId) so a sync-endpoint outage or unexpected
        // payload is debuggable instead of a silent drop.
        log.warn(
          "[sync] failed to fetch unknown chat for incoming message; dropping event",
          { chatId: data.message.chat_id, messageId: data.message.id, err },
        );
        return;
      }
    } else {
      return;
    }
  }

  // `chatDetails` is guaranteed defined here, but TS loses that narrowing for a
  // reassigned `let` captured in the closures below — bind to a const.
  const chat = chatDetails;

  // Guard: if the chat row exists but eCSK is missing (a half-synced chat),
  // bail without persisting anything. The next chat-list sync will deliver a
  // complete row and the original message will be re-fetched then.
  if (data.message.category !== MessageCategory.SERVICE && !chat.eCSK) {
    log.warn("Skipping message — chat row has no eCSK yet", {
      chatId: data.message.chat_id,
      messageId: data.message.id,
    });
    return;
  }

  const result =
    data.message.category === MessageCategory.SERVICE
      ? await tryDecrypt(() => decryptServiceMessageContent(data.message))
      : await tryDecrypt(() =>
          decryptMessageContent(
            chat.id,
            data.message.key_epoch ?? chat.currentEpoch ?? 0,
            chat.eCSK,
            data.message.content,
            data.message.attachment_meta,
          ),
        );

  const decryptionFailed = !result.ok;
  const decryptedContent: MessageContentEntity = result.ok
    ? result.data
    : { text: "" };

  // Update the user avatar map with the sender's latest avatar
  if (data.message.sender && data.message.sender_avatar) {
    useUserAvatarMapState
      .getState()
      .setUserAvatar(
        data.message.sender,
        data.message.sender_avatar,
        extractTimestampFromUUID7(data.message.id),
      );
  }

  const processedMessage: MessageEntity = {
    _clientId: data.message.client_id || data.message.id, // Use client_id if present, otherwise use server id
    id: data.message.id,
    chatId: data.message.chat_id,
    sender: data.message.sender,
    // Read-time anonymisation marker — live pushes never carry it today
    // (fresh senders are alive; service messages are exempt), but every
    // other ingestion path maps it, and a path-dependent boolean is a trap.
    ...(data.message.sender_deleted ? { senderDeleted: true } : {}),
    avatarId: data.message.sender_avatar,
    // Extract timestamp from UUID7 for compatibility
    timestamp: extractTimestampFromUUID7(data.message.id),
    category: data.message.category,
    hasAttachment: !!data.message.attachment_meta,
    content: decryptedContent,
    isEdited: data.message.is_edited,
    isOutgoing: data.message.sender === userFingerPrint.username,
    // thread related
    // commentCount: data.message.comment_count,
    senderAvatar: data.message.sender_avatar,
    parentCommentCount: data.message.parent_comment_count,
    // comment thread
    threadId: data.message.thread_id,
    commentSentAt: null, // No longer needed with UUID7
    // reply related
    isReply: !!data.message.parent_id,
    parentMessage: data.message.parent_id
      ? {
          id: data.message.parent_id,
          category: data.message.parent_category,
        }
      : undefined,
    tags: data.message.tags?.length ? data.message.tags : undefined,

    sendingStatus:
      chat.type === ChatType.P2P
        ? // for p2p chats we mark as DELIVERED (to be updated to READ when we implement read receipts)
          MessageSendingStatus.DELIVERED
        : // for group chats and note to self we can mark as READ
          MessageSendingStatus.READ,

    // When decryption throws we still need to track the message in IDB so
    // the retry orchestrator can find it. Mark it explicitly so the UI
    // shows "Decrypting…" instead of trying to render empty text. Do NOT
    // ever persist the legacy "Could not decrypt message" string here.
    ...(decryptionFailed
      ? {
          decryptionStatus: "failed" as const,
          decryptionAttempts: 1,
        }
      : { decryptionStatus: "ok" as const }),
    keyEpoch: data.message.key_epoch ?? null,
  };

  // Notify the SW when an in-progress upload finishes so it can switch from
  // the pre-assembly endpoint to the assembled file.  Only for incoming messages
  // (sender manages its own SW registration directly).
  // Use serviceWorker.ready so the message is never silently dropped when the
  // controller is null (e.g. first load, SW update pending).
  const incomingUploadState =
    !processedMessage.isOutgoing &&
    processedMessage.content.media?.uploadState?.status;
  if (incomingUploadState === "complete" || incomingUploadState === "failed") {
    const uploadId = processedMessage.content.media!.attachmentId;
    // "complete" → SW switches to the assembled file; "failed" → SW stops
    // streaming/prefetching so the receiver errors promptly instead of polling
    // now-orphaned parts for 90s.
    const type =
      incomingUploadState === "complete" ? "UPLOAD_COMPLETE" : "UPLOAD_FAILED";
    void navigator.serviceWorker?.ready.then((reg) => {
      const sw = navigator.serviceWorker.controller ?? reg.active;
      sw?.postMessage({ type, uploadId });
    });
  }

  switch (data.message.category) {
    case MessageCategory.SERVICE:
      await onCategoryService({
        processedMessage,
        action: data.action,
      });
      break;
    case MessageCategory.MESSAGE:
      await onCategoryMessage({
        processedMessage,
        action: data.action,
        chatDetails: chat,
      });
      break;
    case MessageCategory.COMMENT:
      await onCategoryThreadComment({ processedMessage, action: data.action });
      break;
    // case MessageCategory.REACTION:
    //   onCategoryReaction({
    //     reactionMessage: data.message,
    //     action: data.action,
    //   });
    //   break;
    default:
      break;
  }
};

const onCategoryMessage = async ({
  processedMessage,
  action,
  chatDetails,
}: {
  processedMessage: MessageEntity;
  action: SocketEventActionType;
  chatDetails: ChatEntity;
}) => {
  switch (action) {
    case SocketEventActionType.CREATE:
      {
        const activeChatId = useNavigationState.getState().activeChatId;

        const newUnreadCount =
          activeChatId !== processedMessage.chatId &&
          !processedMessage.isOutgoing
            ? (chatDetails.unreadCount || 0) + 1
            : 0;

        // Update last msg and last message timestamp when the current msg
        // is newer. We persist the failed placeholder too — the parser
        // renders "Decrypting…" based on `decryptionStatus`, and the retry
        // orchestrator's denormalization repair needs `chats.lastMessage.id`
        // to match this row in order to heal it on a successful retry.
        if (
          new Date(processedMessage.timestamp) >
          new Date(chatDetails.lastMessageTimestamp || 0)
        ) {
          await updateChatInLocalDB(processedMessage.chatId, {
            lastMessage: processedMessage,
            lastMessageTimestamp: processedMessage.timestamp,
          });
        }

        await updateChatInLocalDB(processedMessage.chatId, {
          unreadCount: newUnreadCount,
        });

        // if on active chat - updtae last sync timestamp for chat
        if (activeChatId === processedMessage.chatId) {
          if (
            new Date(processedMessage.timestamp) >
            new Date(chatDetails.lastSyncedAt)
          ) {
            // TO DO => THROTTLE this
            await updateChatInLocalDB(processedMessage.chatId, {
              // lastSyncedAt: new Date().toISOString(),
              lastSyncedAt: extractTimestampFromUUID7(processedMessage.id),
            });
          }

          // TO DO => IMPROVE RESET COUNTER LOGIC
          // this will trigger EVERY TIME this event occurs
          await resetChatUnreadMessages({ chatId: activeChatId });
        }

        // TO DO => check if message already has ink metadata attached in local db
        const linkDetails = await processMessageLink({
          messageId: processedMessage.id,
          chatId: processedMessage.chatId,
          timestamp: processedMessage.timestamp,
          link: processedMessage.content.link,
          withMetadata: true,
        });

        if (linkDetails) {
          await addLinkToLocalDB(linkDetails);
        }

        // check if message already exists in local db
        const messageExists = await getMessageByServerIdFromLocalDB(
          processedMessage.id,
        );

        // if message exists - we assume messaage is sent by current user
        if (messageExists) {
          // we can update only status
          await updateMessageByServerIdInLocalDB(messageExists.id, {
            sendingStatus: processedMessage.sendingStatus,
          });
        } else {
          await addMessageToLocalDB(processedMessage);
        }

        // P2P forward secrecy: every live message (my own echo and the peer's
        // messages, all types) flows through here, so this is the single point
        // that advances the CSK epoch by message volume. Only for genuinely
        // live messages — a recency gate keeps catch-up / bulk-sync batches
        // (which replay old timestamps) from running the count scan repeatedly.
        // Fire-and-forget: it must never block or fail message processing.
        const LIVE_MESSAGE_WINDOW_MS = 60_000;
        const isLiveMessage =
          Date.now() - new Date(processedMessage.timestamp).getTime() <
          LIVE_MESSAGE_WINDOW_MS;
        if (chatDetails.type === ChatType.P2P && isLiveMessage) {
          void maybeRotateP2PForForwardSecrecy(processedMessage.chatId);
        }

        // Show native notification only when the app is in the background.
        // Intentionally suppressed when the app is visible (even on a different chat)
        // to avoid OS-level alerts while the user is actively using the app.
        const pageIsVisible = isAppVisible();
        const shouldNotify =
          !processedMessage.isOutgoing &&
          !chatDetails.isMuted &&
          !chatDetails.isArchived &&
          !pageIsVisible;

        if (shouldNotify) {
          log.info("Notification check", { shouldNotify });
          const groupName =
            chatDetails.type === ChatType.GROUP ? chatDetails.name : null;
          void showMessageNotification(
            processedMessage.sender,
            groupName,
            processedMessage.chatId,
          );
        }

        // Play notification sound if message is incoming and chat is not muted
        // if (!processedMessage.isOutgoing && !chatDetails.isMuted) {
        //   try {
        //     await playNotificationSound();
        //   } catch (error) {
        //     // eslint-disable-next-line no-console
        //     console.error("Failed to play notification sound:", error);
        //   }
        // }
      }
      break;
    case SocketEventActionType.UPDATE:
      {
        // const activeChatId = useNavigationState.getState().activeChatId;

        // Update last msg when the edited/updated message is newer, or
        // when it IS the current last message (edit / AI placeholder swap).
        if (
          new Date(processedMessage.timestamp) >
            new Date(chatDetails.lastMessageTimestamp || 0) ||
          processedMessage.id === chatDetails.lastMessage?.id
        ) {
          await updateChatInLocalDB(processedMessage.chatId, {
            lastMessage: processedMessage,
            lastMessageTimestamp: processedMessage.timestamp,
          });
        }

        // TO DO => check if this is actually needed
        // if on active chat - updtae last sync timestamp for chat
        // if (activeChatId === processedMessage.chatId) {
        //   await updateChatInLocalDB(processedMessage.chatId, {
        //     // lastSyncedAt: new Date().toISOString(),
        //     lastSyncedAt: extractTimestampFromUUID7(processedMessage.id),
        //   });

        //   // // TO DO => IMPROVE RESET COUNTER LOGIC
        //   // // this will trigger EVERY TIME this event occurs
        //   // await resetChatUnreadMessages({ chatId: processedMessage.chatId });
        // }

        // check if message exists in local db
        let messageDetails = await getMessageByServerIdFromLocalDB(
          processedMessage.id,
        );

        // INFO: Sometimes the update event beeing processed before the create event finished processing
        // If message details aren't available immediately, implement a retry mechanism with exponential backoff
        if (!messageDetails) {
          // Start with a 200ms initial backoff delay
          let backoffMs = 200;

          // Retry fetching the message with increasing delays until we either:
          // 1. Successfully retrieve the message, or
          // 2. Reach our maximum backoff threshold (500ms)
          while (!messageDetails && backoffMs < 500) {
            // Pause execution for the current backoff duration
            await new Promise((resolve) => setTimeout(resolve, backoffMs));

            // Attempt to fetch the message again from local IndexedDB
            messageDetails = await getMessageByServerIdFromLocalDB(
              processedMessage.id,
            );

            // Increase backoff time by 100ms for next iteration (200ms → 300ms → 400ms)
            backoffMs += 100;
          }

          // If we still couldn't retrieve the message after all retries, abort processing
          if (!messageDetails) {
            return;
          }
        }

        await updateMessageByServerIdInLocalDB(
          processedMessage.id,
          processedMessage,
        );

        // update pinned message content if applicable
        await updatePinnedMessageByServerIdInLocalDB(
          processedMessage.id,
          processedMessage,
        );
      }
      break;
    case SocketEventActionType.DELETE:
      stopChunkedStreamOnDelete(processedMessage);
      await deleteMessageFromLocalDBWithRetry(processedMessage.id);
      // reference to pinned state methods
      // const removePinnedMessageForChat =
      //   usePinnedMessageState.getState().removePinnedMessageForChat;

      // remove associated link from local db
      if (processedMessage.content.link) {
        await deleteLinkByIdFromLocalDB(processedMessage.id);
      }
      // remove associated media from local db
      if (processedMessage.content.media?.attachmentId) {
        await removeMediaAttachmentFromLocalDB(
          processedMessage.content.media?.attachmentId,
        );
      }
      // remove associated file from local db
      if (processedMessage.content.file?.attachmentId) {
        await removeFileAttachmentFromLocalDB(
          processedMessage.content.file?.attachmentId,
        );
      }

      // remove pinned message id if applicable
      await removePinnedMessageByServerIdFromLocalDB(processedMessage.id);
      // Keep `chat.pinnedMessageIds` (single source of truth the new-sync
      // reconcile enforces) authoritative: a deleted message must also leave the
      // chat's pinned set, else the next reconcile re-adds/re-fetches it.
      await removePinnedMessageIdFromChatInLocalDB(
        processedMessage.chatId,
        processedMessage.id,
      );

      // check if chat last message is the same as the one deleted
      if (chatDetails.lastMessage?.id === processedMessage.id) {
        // get the latest message from the chat
        const newLastMessage = await getLatestMessagesByChatIdFromLocalDB(
          chatDetails.id,
          1,
        );

        if (
          newLastMessage[0] &&
          newLastMessage[0].category === MessageCategory.MESSAGE
        ) {
          await updateChatInLocalDB(processedMessage.chatId, {
            lastMessage: newLastMessage[0],
            lastMessageTimestamp: newLastMessage[0].timestamp,
          });
        } else {
          await updateChatInLocalDB(processedMessage.chatId, {
            lastMessage: undefined,
            lastMessageTimestamp: undefined,
          });
        }
      }

      // TO DO => Check if is thread and delete all relaetd comments from localDB
      break;
    default:
      break;
  }
};

const updateCommentSyncTimestamp = async (
  processedMessage: MessageEntity,
): Promise<void> => {
  const { activeChatId, activeThreadId } = useNavigationState.getState();

  const isActiveThread =
    activeThreadId === processedMessage.threadId &&
    activeChatId === processedMessage.chatId;

  if (isActiveThread && processedMessage.threadId) {
    await updateMessageByServerIdInLocalDB(processedMessage.threadId, {
      // commentLastSyncedAt: new Date().toISOString(),
      commentLastSyncedAt: extractTimestampFromUUID7(processedMessage.id),
    });
  }
};

// TO DO => check if thids is still after new reactions implementation and cheking for message status in message context menu
const waitForMessageInLocalDB = async (
  messageId: string,
): Promise<MessageEntity | undefined | null> => {
  let messageDetails = await getMessageByServerIdFromLocalDB(messageId);

  if (messageDetails) {
    return messageDetails;
  }

  // INFO: Sometimes the update event beeing processed before the create event finished processing
  // If message details aren't available immediately, implement a retry mechanism with exponential backoff
  // Start with a 200ms initial backoff delay
  let backoffMs = 200;

  // Retry fetching the message with increasing delays until we either:
  // 1. Successfully retrieve the message, or
  // 2. Reach our maximum backoff threshold (500ms)
  while (!messageDetails && backoffMs < 500) {
    // Pause execution for the current backoff duration
    await new Promise((resolve) => setTimeout(resolve, backoffMs));

    // Attempt to fetch the message again from local IndexedDB
    messageDetails = await getMessageByServerIdFromLocalDB(messageId);

    // Increase backoff time by 100ms for next iteration (200ms → 300ms → 400ms)
    backoffMs += 100;
  }

  return messageDetails;
};

const handleThreadCommentCreate = async (
  processedMessage: MessageEntity,
): Promise<void> => {
  // threadLastCommentTimestamp logic removed - field no longer exists in MessageEntity

  // if on active chat=>comment - update last sync timestamp for comment msg
  await updateCommentSyncTimestamp(processedMessage);

  // TO DO => Decide if comment messages should reset chat unread counter
  // TO DO => IMPROVE RESET COUNTER LOGIC
  // this will trigger EVERY TIME this event occurs
  // await resetChatUnreadMessages({ chatId: activeChatId });

  // // add message to local db
  // await addMessageToLocalDB(processedMessage);

  // TO DO => check if message already has link metadata attached in local db
  const linkDetails = await processMessageLink({
    messageId: processedMessage.id,
    chatId: processedMessage.chatId,
    timestamp: processedMessage.timestamp,
    link: processedMessage.content.link,
    withMetadata: true,
  });

  if (linkDetails) {
    await addLinkToLocalDB(linkDetails);
  }

  // check if comment already exists in local db
  const commentExists = await getMessageByServerIdFromLocalDB(
    processedMessage.id,
  );

  if (commentExists) {
    // we can update only status
    await updateMessageByServerIdInLocalDB(commentExists.id, {
      sendingStatus: processedMessage.sendingStatus,
    });
  } else {
    await addMessageToLocalDB(processedMessage);
  }

  // update parent comment count
  // TO DO => this is temporary untill we fix "missed events" issue
  // concurency on message category events -
  // slight delay to ensure parent message is updated after creation
  if (processedMessage.threadId) {
    // setTimeout(() => {
    //   updateMessageInLocalDB(processedMessage.threadId || "", {
    //     commentCount: processedMessage.parentCommentCount,
    //   });
    // }, 500);

    await updateMessageByServerIdInLocalDB(processedMessage.threadId, {
      commentCount: processedMessage.parentCommentCount,
    });
  }
};

const handleThreadCommentUpdate = async (
  processedMessage: MessageEntity,
): Promise<void> => {
  // if on active chat=>comment - update last sync timestamp for comment msg
  await updateCommentSyncTimestamp(processedMessage);
  // TO DO => Decide if comment messages should reset chat unread counter
  // TO DO => IMPROVE RESET COUNTER LOGIC
  // this will trigger EVERY TIME this event occurs
  // await resetChatUnreadMessages({ chatId: activeChatId });

  // check if message exists in local db
  const messageDetails = await waitForMessageInLocalDB(processedMessage.id);

  // If we still couldn't retrieve the message after all retries, abort processing
  if (!messageDetails) {
    return;
  }

  await updateMessageByServerIdInLocalDB(processedMessage.id, processedMessage);
};

const handleThreadCommentDelete = async (
  processedMessage: MessageEntity,
): Promise<void> => {
  stopChunkedStreamOnDelete(processedMessage);
  await deleteMessageFromLocalDBWithRetry(processedMessage.id);

  // remove associated link from local db
  if (processedMessage.content.link) {
    await deleteLinkByIdFromLocalDB(processedMessage.id);
  }
  // remove associated media from local db
  if (processedMessage.content.media?.attachmentId) {
    await removeMediaAttachmentFromLocalDB(
      processedMessage.content.media?.attachmentId,
    );
  }
  // remove associated file from local db
  if (processedMessage.content.file?.attachmentId) {
    await removeFileAttachmentFromLocalDB(
      processedMessage.content.file?.attachmentId,
    );
  }

  // update parent comment count
  if (processedMessage.threadId) {
    await updateMessageByServerIdInLocalDB(processedMessage.threadId, {
      commentCount: processedMessage.parentCommentCount,
    });
  }
};

const onCategoryThreadComment = async ({
  processedMessage,
  action,
}: {
  processedMessage: MessageEntity;
  action: SocketEventActionType;
}): Promise<void> => {
  switch (action) {
    case SocketEventActionType.CREATE:
      await handleThreadCommentCreate(processedMessage);
      break;
    case SocketEventActionType.UPDATE:
      await handleThreadCommentUpdate(processedMessage);
      break;
    case SocketEventActionType.DELETE:
      await handleThreadCommentDelete(processedMessage);
      break;
    default:
      break;
  }
};

const onCategoryService = async ({
  processedMessage,
  action,
}: {
  processedMessage: MessageEntity;
  action: SocketEventActionType;
}) => {
  switch (action) {
    case SocketEventActionType.CREATE: {
      // get local db chat
      const chatDetails = await getChatFromLocalDB(processedMessage.chatId);

      // get user role in chat
      const userRoleInChat = chatDetails?.roleAsMember.role;

      const includeSystemMessages =
        chatDetails?.type === ChatType.GROUP &&
        userRoleInChat !== MemberRole.MEMBER;

      // add service message only if user is not a member
      if (includeSystemMessages) {
        await addMessageToLocalDB(processedMessage);
      }

      const activeChatId = useNavigationState.getState().activeChatId;

      // if on active chat - updtae last sync timestamp for chat
      if (activeChatId === processedMessage.chatId) {
        await updateChatInLocalDB(processedMessage.chatId, {
          // lastSyncedAt: new Date().toISOString(),
          lastSyncedAt: extractTimestampFromUUID7(processedMessage.id),
        });
      }

      break;
    }
    case SocketEventActionType.UPDATE:
      break;
    case SocketEventActionType.DELETE:
      break;
    default:
      break;
  }
};

/**
 * Handles reaction events for messages with debouncing to prevent excessive updates.
 *
 * How it works:
 * 1. Receives reaction message and action type (add/delete)
 * 2. Clears any existing pending update timeout for the same parent message
 * 3. Sets a new timeout with appropriate delay based on action type
 * 4. Updates the message with new reaction data when timeout completes
 * 5. Cleans up the pending update reference after completion
 *
 * Key features:
 * - Debounces reaction updates to prevent UI flickering and reduce database operations
 * - Uses different delays based on action type (immediate for adds, delayed for deletes)
 * - Handles cleanup to prevent memory leaks
 * - Ensures reaction updates complete even if errors occur
 */
// const pendingReactionUpdates: Record<string, NodeJS.Timeout> = {};

/**
 * Processes message reaction events with debouncing to optimize performance.
 *
 * @param reactionMessage - The encrypted message containing reaction data
 * @param action - The type of action (add or delete)
 */
// const onCategoryReaction = async ({
//   reactionMessage,
//   action,
// }: {
//   reactionMessage: EncryptedMessage;
//   action: SocketEventActionType;
// }) => {
//   // Extract the parent message ID that received the reaction
//   const parentId = reactionMessage.parent_id;

//   // Clear any existing timeout for this message to prevent duplicate updates
//   if (pendingReactionUpdates[parentId]) {
//     clearTimeout(pendingReactionUpdates[parentId]);
//   }

//   // Use a delay for DELETE actions to allow for potential cancellations or batching
//   // For ADD actions, process immediately (0ms delay)
//   const delay = action === SocketEventActionType.DELETE ? 1000 : 0;

//   // Set a new timeout (debounce for the specified delay)
//   pendingReactionUpdates[parentId] = setTimeout(async () => {
//     try {
//       // Update the message with the latest reaction data
//       await updateMessageOnReactionEvent(parentId);
//     } finally {
//       // Clean up the pending update reference to prevent memory leaks
//       // This ensures cleanup happens even if an error occurs during the update
//       delete pendingReactionUpdates[parentId];
//     }
//   }, delay);
// };
